<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

```tsx import
import {Flex, Grid} from '@react-spectrum/layout';
import {View} from '@react-spectrum/view';
import {Picker, Item} from '@react-spectrum/picker';
import Book from '@spectrum-icons/workflow/Book';
import BulkEditUsers from '@spectrum-icons/workflow/BulkEditUsers';
import Draw from '@spectrum-icons/workflow/Draw';
import {Text} from '@react-spectrum/typography';
```

---
category: Concepts
---

# Layout

This page describes how to build application layouts with React Spectrum using Flex or Grid,
as well as how to integrate with pre-defined Spectrum layouts using slots.

## Introduction

React Spectrum includes layout components to help build Spectrum compliant applications more easily.
The [Flex](Flex.html) and [Grid](Grid.html) components are containers, which are responsible for
the layout of their children. `Flex` follows the 
[CSS flexbox](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Flexible_Box_Layout/Basic_Concepts_of_Flexbox)
algorithm, while `Grid` implements
[CSS grid](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout/Basic_Concepts_of_Grid_Layout).
These components provide props with pre-defined Spectrum variables for sizing, spacing,
and other layout options. You can use `Flex` and `Grid` together as needed to build different
parts of your application, and even nest them to create more complex layouts.

In general, you should prefer using flex and grid over other CSS layout models. Spacing between components
should be left to parent layout components rather than added as via margins to specific children. This helps
ensure components are composable when reused in different places, and ensures that spacing is consistent.

In addition to `Flex` and `Grid`, some React Spectrum components include pre-built layouts which you can 
insert your content into via slots. This often happens automatically through the use of semantic elements,
but can also be configured through props. For example, the [Dialog](Dialog.html) component accepts 
[Heading](Heading.html), [Content](Content.html), and [Footer](Footer.html) elements as children and 
will handle the layout automatically.

## Flex

The [flexbox](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Flexible_Box_Layout/Basic_Concepts_of_Flexbox)
layout model is a simple yet versatile method of laying out components in rows and columns.
You can use it to build vertical or horizontal stacks, simple wrapping grids, and more. The [Flex](Flex.html)
component can be used to create flexbox containers, and any React Spectrum component can be used as a child.
Flex layouts are also automatically mirrored in right-to-left languages, and can be nested to create 
more complex layouts.

In addition to the properties widely supported by CSS, React Spectrum also shims the `gap` property, along
with `rowGap` and `columnGap`. These properties make it much easier to build layouts
with consistent space between each item. The gap can be defined with Spectrum [dimension variables](styling.html#dimension-values)
to ensure consistency across applications, and allow the layout to adapt to different devices automatically.

### Example

This example shows a simple horizontal stack, with a gap between each item defined using a Spectrum dimension variable.

```tsx example
<Flex direction="column" width="size-2000" gap="size-100">
  <View backgroundColor="celery-600" height="size-800" />
  <View backgroundColor="blue-600" height="size-800" />
  <View backgroundColor="magenta-600" height="size-800" />
</Flex>
```

### Learn more

You can learn more about `Flex` and see more examples on the [Flex](Flex.html) page. In addition,
there are many great resources on the web for learning flexbox.

* [The MDN guide to flexbox](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Flexible_Box_Layout) — full walkthrough
of flexbox layout.
* [A Complete Guide to Flexbox](https://css-tricks.com/snippets/css/a-guide-to-flexbox/) — great reference for all of the
properties supported by flexbox.

## Grid

[CSS grid](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout/Basic_Concepts_of_Grid_Layout) 
is a powerful way to lay out elements in two dimensions. It can be used to to build full page application
layouts, or smaller user interface elements. It is especially powerful because it allows you to build many types of 
layouts without extra presentational elements, keeping your code clean and semantic. In addition, grid layouts
are automatically mirrored in right-to-left languages.

The [Grid](Grid.html) component can be used as a container to define a grid layout. Any React Spectrum component can be used as
a child of a `Grid`. The `Grid` component extends the CSS syntax to support defining grids using Spectrum defined dimension values.
This ensures that sizing and spacing is consistent between applications, and allows the layout to adapt to different devices
automatically.

### Defining grids

There are many ways to define grids, but the simplest is to use the `areas` prop to declaratively
specify your layout using named areas. This prop accepts an array of strings which represent rows. Within the rows,
you specify space separated names for grid areas. The children of the `Grid` can declare the `gridArea` prop, which
places them into these named regions.

In addition, you can define the `columns` and `rows`
props on the `Grid` container to specify the widths and heights of the columns and rows respectively. This can be 
done using Spectrum dimension values to ensure they are adaptive on various devices.

The following example shows how you could use `Grid` to declare a common application layout, with a header, sidebar,
content area, and footer. Notice how there are no nested layout elements — the layout is entirely declared in the
`Grid` and the children simply declare where they should be placed.

```tsx example
<Grid
  areas={[
    'header  header',
    'sidebar content',
    'footer  footer'
  ]}
  columns={['size-3000', 'auto']}
  rows={['size-1000', 'auto', 'size-1000']}
  height="size-6000"
  gap="size-100">
  <View backgroundColor="celery-600" gridArea="header" />
  <View backgroundColor="blue-600" gridArea="sidebar" />
  <View backgroundColor="purple-600" gridArea="content" />
  <View backgroundColor="magenta-600" gridArea="footer" />
</Grid>
```

### Learn more

You can learn more about `Grid` and see more examples on the [Grid](Grid.html) page. In addition,
there are many great resources on the web for learning CSS grid.

* [The MDN guide to CSS grid](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout) — full walkthrough of
grid layout.
* [A Complete Guide to Grid](https://css-tricks.com/snippets/css/complete-guide-grid) — great reference for all of the
properties supported by grid.
* [The Difference Between Explicit and Implicit Grids](https://css-tricks.com/difference-explicit-implicit-grids/) — an
article that discusses the various ways of defining grids.

## Slots

Several React Spectrum components include pre-defined layouts that you can insert elements into via slots.
Slots are named areas in a component that receive children and provide style and layout for them. This
often occurs automatically through the use of semantic elements like [Header](Header.html), [Content](Content.html),
and [Footer](Footer.html), which include default slots out of the box.

Slots and semantic elements allow you to let Spectrum define the complex layouts required by many components according
to design requirements automatically. You just provide the proper child elements, and the container component handles the 
layout and styling of the children for you.

For example, the [Dialog](Dialog.html) component accepts many types of children, e.g. a [Heading](Heading.html)
for the title, a [Content](Content.html) element for the body, a [ButtonGroup](ButtonGroup.html) for the buttons, etc.,
and `Dialog` takes care of positioning and styling them properly for the dialog context. In addition, the layout of 
these items can change depending on the screen size. For example, the buttons in full screen dialogs move from the 
top to the bottom on mobile.

Another example of this is the complex items supported by [Picker](Picker.html) and [Menu](Menu.html). These components allow
icons, text, and descriptions to be added as children of items, and the layout and styling is handled automatically.
The icon and main text element of an item are placed in slots automatically, and the description text element is
placed using the `slot` prop to specify a named area.

```tsx example
<Picker label="Permission" defaultSelectedKey="read">
  <Item textValue="Read" uniqueKey="read">
    <Book size="S" />
    <Text>Read</Text>
    <Text slot="description">Read only</Text>
  </Item>
  <Item textValue="Write" uniqueKey="write">
    <Draw size="S" />
    <Text>Write</Text>
    <Text slot="description">Read and write only</Text>
  </Item>
  <Item textValue="Admin" uniqueKey="admin">
    <BulkEditUsers size="S" />
    <Text>Admin</Text>
    <Text slot="description">Full access</Text>
  </Item>
</Picker>
```

You can learn more about the slots and semantic elements supported by each component by reading their individual
documentation pages.
